import React, { useCallback, useMemo } from 'react';
import { Card } from '../Card/Card';
import { useAppOrientation } from '@/lostHooks/orientation/useOrientation';
import { useRecentlyPlayed } from '@/api/recently-played/use-recently-played';
import { getRecentlyPlayedUrl } from '@/utilities/helpers/getRecentlyPlayedUrl';
import { usePopup } from '@/features/Popup/usePopup';
import { generatePopupConfig } from '@/features/Popup/helpers/generatePopupConfig';
import { POPUP_TYPES } from '@/features/Popup/constants';
import { Loader } from '@fanduel/formation-react-components';
import './styles/recentlyPlayed.scss';
import { useStore } from '@/store/useStore';
import { setRecentlyPlayedContext } from '@/store/actions';
import { useShallow } from 'zustand/react/shallow';
import { RecentlyPlayedItem } from '@/store/slices/recentlyPlayed/recentlyPlayed';

import { SHOWOFF_ELEMENTS } from '@/features/Showoff';

interface RecentlyPlayedProps {
  width?: string;
  recalculateSize?: () => void;
}

export const RecentlyPlayed: React.FC<RecentlyPlayedProps> = ({
  recalculateSize,
}) => {
  const orientation = useAppOrientation();

  const gameUid = useStore((s) => s.gameContext.gameUid);
  const { recentlyPlayedContext, recentlyPlayedStatus, recentlyPlayedError } =
    useStore(
      useShallow((s) => ({
        recentlyPlayedContext: s.recentlyPlayedContext,
        recentlyPlayedStatus: s.recentlyPlayedStatus,
        recentlyPlayedError: s.recentlyPlayedError,
      })),
    );

  const recentlyPlayedRaw = recentlyPlayedContext.recentlyPlayed;
  const loading = recentlyPlayedStatus.loading;
  const error = recentlyPlayedError;
  const { showPopup } = usePopup();

  useRecentlyPlayed();

  const recentlyPlayed = useMemo(
    () =>
      recentlyPlayedRaw && recentlyPlayedRaw.length > 0
        ? recentlyPlayedRaw.map((item) => ({
            ...item,
            gameId: item?.gameId || item?.prismicCode,
          }))
        : [],
    [recentlyPlayedRaw],
  );

  // Skip current game and slice till next 4

  const lastRecentlyPlayedItems = recentlyPlayed
    .filter((game) => game.gameUid !== gameUid)
    .slice(0, 4);

  const itemCount = lastRecentlyPlayedItems.length;

  const contentWidth = orientation === 'portrait' ? '100%' : '420px';
  const contentHeight = orientation === 'portrait' ? '162px' : '100%';

  const handleCardClick = useCallback(
    (item: RecentlyPlayedItem) => {
      // Persist the selected game so the standard EXIT_GAME_RECENTLY_PLAYED
      // action (useButtonAction -> launchNextGame) can read it from the store.
      setRecentlyPlayedContext({
        nextGameId: item?.gameId ?? null,
        nextGameUid: item?.gameUid ?? null,
        nextGameUrl: getRecentlyPlayedUrl(item),
      });

      const popupConfig = generatePopupConfig(POPUP_TYPES.EXIT_RECENTLY);

      showPopup(popupConfig, POPUP_TYPES.EXIT_RECENTLY);
    },
    [showPopup],
  );

  if (itemCount === 0 && !loading) {
    return null;
  }

  const hasSliceError =
    (typeof error === 'string' && error === 'Error') ||
    Boolean(
      error &&
      typeof error === 'object' &&
      'type' in error &&
      (error as { type: string | null }).type,
    );
  if (hasSliceError) {
    return null;
  }

  return (
    <Card
      action={(e) => e.preventDefault()}
      variant={'title'}
      data-showoff={SHOWOFF_ELEMENTS.RECENTLY_PLAYED_CARD}
      data-showoff-badge="false"
    >
      <div className="recently-played__title">Recently Played Games</div>
      {itemCount > 0 ? (
        <div
          className={`recently-played__container recently-played__container--${itemCount}`}
          id="game-window-recently-played-container"
        >
          {lastRecentlyPlayedItems.map((item, index) => {
            return (
              <Card
                key={item?.gameLaunchCode || index}
                variant="image"
                image={item?.imagePath}
                imageAlt={item?.displayName}
                title={item?.displayName}
                onImageLoad={recalculateSize}
                action={(e) => {
                  e.preventDefault();
                  handleCardClick(item);
                }}
                className="recently-played__card"
                dataTestId="recently-played-card"
              />
            );
          })}
        </div>
      ) : (
        <div
          className="recently-played__loader"
          style={{
            width: contentWidth,
            height: contentHeight,
          }}
        >
          <Loader size="md" inverse />
        </div>
      )}
    </Card>
  );
};
